import { createPortal } from 'react-dom';
import type { AdminAnalyticsReport, AttributionSummary, ExecutiveInsight, JourneySummary } from '../types';
import { STATUS_COLORS, STATUS_LABELS } from './EodExecutiveReport';
import { sourceLabel } from '../utils/source-label';

// Statuses with configured reason options in the CRM.
const STATUSES_WITH_REASONS = ['contacted', 'qualified', 'won', 'lost'];
const OUTCOME_ORDER = ['contacted', 'follow_up', 'qualified', 'won', 'lost', 'new'];

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
}

function statusColor(status: string): string {
  return STATUS_COLORS[status] ?? '#94a3b8';
}

// Lighter tint of a hex color for backgrounds
function statusTint(status: string): string {
  const tints: Record<string, string> = {
    contacted: '#eff6ff',
    follow_up: '#fffbeb',
    qualified: '#f5f3ff',
    won: '#ecfdf5',
    lost: '#fef2f2',
    new: '#f8fafc',
  };
  return tints[status] ?? '#f8fafc';
}

function num(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function pct(count: number, total: number): string {
  if (total <= 0) return '0%';
  return `${((count / total) * 100).toFixed(1)}%`;
}

function pctNum(count: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((count / total) * 1000) / 10;
}

export function formatDate(iso?: string | null): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(new Date(iso));
}

export function formatTime(iso?: string | null): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).format(new Date(iso));
}

// ============================================================================
// TYPES
// ============================================================================

type StatusReasonEntry = { status: string; total: number; reasons: Array<{ reason: string; count: number; percentage: number }> };
type PipelineEntry = { status: string; count: number };
type WorkTypeEntry = { type: string; label: string; attempts: number; unique: number };
type AgentRow = {
  telecaller_id: string;
  telecaller_name: string;
  total_call_attempts: number;
  unique_leads_called?: number;
  connected_calls: number;
  not_connected_calls: number;
  connection_rate?: number;
  new_lead_call_attempts?: number;
  old_assigned_today_attempts?: number;
  follow_up_call_attempts?: number;
  assigned_today?: number;
};

type EodSummary = {
  new_today?: number;
  unassigned_today?: number;
  total_unassigned?: number;
  assigned_today?: number;
  total_call_attempts?: number;
  unique_leads_called?: number;
  connected_calls?: number;
  not_connected_calls?: number;
  lost_leads?: number;
};

type EodChartData = {
  pipeline?: PipelineEntry[];
  pipeline_total?: number;
  status_reason_breakdown?: StatusReasonEntry[];
  work_type?: WorkTypeEntry[];
  journey_summary?: JourneySummary;
  attribution_summary?: AttributionSummary;
  executive_insights?: ExecutiveInsight[];
};

// ============================================================================
// DERIVED INSIGHT
// ============================================================================

function deriveInsight(
  status: string,
  count: number,
  reasons: Array<{ reason: string; count: number; percentage: number }>,
): string | null {
  if (count <= 0 || reasons.length === 0) return null;
  const top = reasons[0];
  if (top.count <= 0) return null;
  if (status === 'lost') {
    return `${top.count} of ${count} lost leads (${top.percentage}%) were marked "${top.reason}".`;
  }
  if (status === 'won') {
    return `${top.count} of ${count} won leads (${top.percentage}%) were marked "${top.reason}".`;
  }
  if (status === 'contacted') {
    return `${top.count} of ${count} contacted leads (${top.percentage}%) — "${top.reason}".`;
  }
  if (status === 'qualified') {
    return `${top.count} of ${count} qualified leads (${top.percentage}%) — "${top.reason}".`;
  }
  return `${top.count} of ${count} ${statusLabel(status).toLowerCase()} leads (${top.percentage}%) — "${top.reason}".`;
}

// ============================================================================
// PRINT REPORT — A4 optimized, full color, multi-page, premium design
// ============================================================================

export default function EodExecutiveReportPrint({ report }: { report: AdminAnalyticsReport }) {
  const summary = (report.overall_summary ?? {}) as EodSummary;
  const charts = (report.chart_data ?? {}) as EodChartData;
  const agents = (report.telecaller_summary ?? []) as unknown as AgentRow[];

  const pipeline = charts.pipeline ?? [];
  const pipelineTotal = num(charts.pipeline_total ?? summary.new_today ?? 0);
  const statusReasonBreakdown = charts.status_reason_breakdown ?? [];
  const workType = charts.work_type ?? [];
  const journey = charts.journey_summary;
  const attribution = charts.attribution_summary;
  const insights = charts.executive_insights ?? [];
  const connected = num(summary.connected_calls);
  const notConnected = num(summary.not_connected_calls);
  const totalCalls = num(summary.total_call_attempts);
  const uniqueCalled = num(summary.unique_leads_called);
  const newToday = num(summary.new_today);
  const totalUnassigned = num(summary.total_unassigned);
  const unassignedToday = num(summary.unassigned_today);
  const lostCount = pipeline.find((p) => p.status === 'lost')?.count ?? 0;
  const lostRate = pctNum(lostCount, pipelineTotal);
  const connRate = uniqueCalled > 0 ? Math.round((connected / uniqueCalled) * 1000) / 10 : 0;
  const assignedRate = newToday > 0 ? pctNum(newToday - unassignedToday, newToday) : 0;

  // Ordered outcomes for flow visual.
  const outcomes = OUTCOME_ORDER
    .map((s) => pipeline.find((p) => p.status === s && p.count > 0))
    .filter((o): o is PipelineEntry => !!o)
    .concat(pipeline.filter((p) => p.status !== 'unassigned' && p.count > 0 && !OUTCOME_ORDER.includes(p.status)));
  const maxOutcome = Math.max(...outcomes.map((o) => o.count), 1);

  // Reason-supported statuses that have data.
  const reasonStatuses = STATUSES_WITH_REASONS
    .map((s) => ({
      status: s,
      entry: statusReasonBreakdown.find((sr) => sr.status === s),
      pipeline: pipeline.find((p) => p.status === s),
    }))
    .filter((x) => x.pipeline && x.pipeline.count > 0);

  const kpis = [
    { label: 'New Lead Records', value: newToday, sub: `${assignedRate}% assigned · incl. inbound call rings`, accent: '#64748b', tint: '#f1f5f9' },
    { label: 'Call Attempts', value: totalCalls, sub: `${uniqueCalled} unique patients · in + out`, accent: '#2563eb', tint: '#eff6ff' },
    { label: 'Unique Patients', value: uniqueCalled, sub: `${totalCalls} attempts`, accent: '#0ea5e9', tint: '#f0f9ff' },
    { label: 'Patient Connect Rate', value: `${connRate}%`, sub: `${connected} of ${uniqueCalled} answered`, accent: '#2563eb', tint: '#eff6ff' },
    { label: 'Lost Leads', value: lostCount, sub: `${lostRate}% of today`, accent: '#e11d48', tint: '#fef2f2' },
    { label: 'Unassigned', value: totalUnassigned, sub: `${unassignedToday} from today`, accent: '#f97316', tint: '#fff7ed' },
  ];

  const dateStr = formatDate(report.window_start);

  return createPortal(
    <>
      <style>{PRINT_CSS}</style>
      <div className="eod-print-report">
        {/* ===== PAGE 1: Header + KPIs + Lead Flow ===== */}
        <div className="eod-print-page">
          {/* Premium header banner */}
          <div className="print-header-banner">
            <div className="print-header-brand">
              <div className="print-brand-mark">SC</div>
              <div>
                <div className="print-brand-name">Santaan CRM</div>
                <div className="print-brand-sub">Executive Daily Report</div>
              </div>
            </div>
            <div className="print-header-meta">
              <h1>EOD Performance Report</h1>
              <div className="print-date">{dateStr}</div>
              <div className="print-time">
                {formatTime(report.window_start)} — {formatTime(report.window_end)} IST
              </div>
            </div>
          </div>
          {report.generated_at && (
            <div className="print-generated-bar">
              Generated: {formatDate(report.generated_at)} {formatTime(report.generated_at)} IST
            </div>
          )}

          {/* KPI cards */}
          <div className="print-section-label">
            <span className="print-section-label-bar" />
            Executive Summary
          </div>
          <div className="print-kpi-grid">
            {kpis.map((k) => (
              <div key={k.label} className="print-kpi-card" style={{ borderLeftColor: k.accent, background: k.tint }}>
                <div className="print-kpi-label">{k.label}</div>
                <div className="print-kpi-value" style={{ color: k.accent }}>{k.value}</div>
                <div className="print-kpi-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Lead flow visual */}
          <div className="print-section-label print-section-spacing">
            <span className="print-section-label-bar" />
            Today's Lead Flow
          </div>
          <div className="print-flow-container">
            <div className="print-flow-total">
              <span className="print-flow-total-num">{pipelineTotal}</span>
              <span className="print-flow-total-label">Leads Generated Today</span>
            </div>
            <div className="print-flow-connector" />
            <div className="print-flow-outcomes">
              {outcomes.map((o) => {
                const widthPct = Math.max((o.count / maxOutcome) * 100, 12);
                return (
                  <div key={o.status} className="print-flow-row">
                    <div className="print-flow-label">{statusLabel(o.status)}</div>
                    <div className="print-flow-bar-track">
                      <div
                        className="print-flow-bar"
                        style={{ width: `${widthPct}%`, backgroundColor: statusColor(o.status) }}
                      >
                        <span className="print-flow-bar-num">{o.count}</span>
                      </div>
                    </div>
                    <div className="print-flow-count">{o.count}</div>
                    <div className="print-flow-pct">{pct(o.count, pipelineTotal)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="print-page-footer">
            <span>Santaan CRM · EOD Performance Report · {dateStr}</span>
          </div>
        </div>

        {/* ===== PAGE 2: Outcome Reason Analysis ===== */}
        <div className="eod-print-page">
          <div className="print-section-label">
            <span className="print-section-label-bar" />
            Outcome Reason Analysis
          </div>
          <p className="print-section-subtitle">
            Why today's leads ended up in each current status
          </p>

          {reasonStatuses.length === 0 && (
            <div className="print-empty">No reason data available for today's outcomes.</div>
          )}

          {reasonStatuses.map(({ status, entry, pipeline: pipe }) => {
            const count = pipe?.count ?? 0;
            const reasons = entry?.reasons ?? [];
            const maxReason = reasons.length > 0 ? Math.max(...reasons.map((r) => r.count), 1) : 1;
            const insight = deriveInsight(status, count, reasons);
            const hasNoReasonData = reasons.length === 0;

            return (
              <div key={status} className="print-reason-block" style={{ borderTopColor: statusColor(status) }}>
                <div className="print-reason-header" style={{ background: statusTint(status) }}>
                  <span className="print-reason-dot" style={{ backgroundColor: statusColor(status) }} />
                  <span className="print-reason-status">{statusLabel(status)}</span>
                  <span className="print-reason-count">{count} leads · {pct(count, pipelineTotal)}</span>
                </div>

                <div className="print-reason-body">
                  <div className="print-reason-question">
                    Why were leads {statusLabel(status).toLowerCase()}?
                  </div>

                  {hasNoReasonData ? (
                    <div className="print-reason-empty">
                      No reasons recorded for {statusLabel(status)} leads.
                    </div>
                  ) : (
                    <div className="print-reason-bars">
                      {reasons.map((r) => (
                        <div key={r.reason} className="print-reason-row">
                          <div className="print-reason-name">{r.reason}</div>
                          <div className="print-reason-bar-track">
                            <div
                              className="print-reason-bar"
                              style={{ width: `${(r.count / maxReason) * 100}%`, backgroundColor: statusColor(status) }}
                            />
                          </div>
                          <div className="print-reason-num">{r.count}</div>
                          <div className="print-reason-pct">{r.percentage}%</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {insight && (
                    <div className="print-key-finding" style={{ borderLeftColor: statusColor(status) }}>
                      <span className="print-key-finding-label">Key Finding</span>
                      <span className="print-key-finding-text">{insight}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className="print-page-footer">
            <span>Santaan CRM · EOD Performance Report · {dateStr}</span>
          </div>
        </div>

        {/* ===== PAGE 3: Calling Performance + Work Mix ===== */}
        <div className="eod-print-page">
          <div className="print-section-label">
            <span className="print-section-label-bar" />
            Calling Performance
          </div>
          <div className="print-calling-grid">
            <div className="print-metric" style={{ background: '#eff6ff', borderLeftColor: '#2563eb' }}>
              <div className="print-metric-label">Call Attempts</div>
              <div className="print-metric-value" style={{ color: '#2563eb' }}>{totalCalls}</div>
            </div>
            <div className="print-metric" style={{ background: '#f0f9ff', borderLeftColor: '#0ea5e9' }}>
              <div className="print-metric-label">Unique Patients</div>
              <div className="print-metric-value" style={{ color: '#0ea5e9' }}>{uniqueCalled}</div>
            </div>
            <div className="print-metric" style={{ background: '#ecfdf5', borderLeftColor: '#10b981' }}>
              <div className="print-metric-label">Patients Connected</div>
              <div className="print-metric-value" style={{ color: '#10b981' }}>{connected}</div>
            </div>
            <div className="print-metric" style={{ background: '#fef2f2', borderLeftColor: '#e11d48' }}>
              <div className="print-metric-label">Patients No Answer</div>
              <div className="print-metric-value" style={{ color: '#e11d48' }}>{notConnected}</div>
            </div>
            <div className="print-metric" style={{ background: '#f5f3ff', borderLeftColor: '#8b5cf6' }}>
              <div className="print-metric-label">Connection Rate</div>
              <div className="print-metric-value" style={{ color: '#8b5cf6' }}>{connRate}%</div>
            </div>
          </div>

          {/* Work mix */}
          {workType.length > 0 && workType.some((w) => w.attempts > 0) && (
            <>
              <div className="print-subsection-title">Work Mix</div>
              <table className="print-table">
                <thead>
                  <tr>
                    <th className="print-th-left">Work Type</th>
                    <th className="print-th-right">Unique Leads</th>
                    <th className="print-th-right">Attempts</th>
                  </tr>
                </thead>
                <tbody>
                  {workType.map((w) => (
                    <tr key={w.type}>
                      <td className="print-td-left">{w.label}</td>
                      <td className="print-td-right">{w.unique}</td>
                      <td className="print-td-right">{w.attempts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="print-page-footer">
            <span>Santaan CRM · EOD Performance Report · {dateStr}</span>
          </div>
        </div>

        {/* ===== PAGE 4+: Agent Performance ===== */}
        <div className="eod-print-page">
          <div className="print-section-label">
            <span className="print-section-label-bar" />
            Agent Performance
          </div>
          <p className="print-section-subtitle">Detailed breakdown by telecaller</p>

          {agents.length > 0 ? (
            <table className="print-table print-agent-table">
              <thead>
                <tr>
                  <th className="print-th-left">Agent</th>
                  <th className="print-th-right">Attempts</th>
                  <th className="print-th-right">Unique Patients</th>
                  <th className="print-th-right">Connected</th>
                  <th className="print-th-right">No Answer</th>
                  <th className="print-th-right">Conn. Rate</th>
                  <th className="print-th-right">New Leads</th>
                  <th className="print-th-right">Old Assigned</th>
                  <th className="print-th-right">Follow-ups</th>
                  <th className="print-th-right">Assigned Today</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((row, i) => (
                  <tr key={row.telecaller_id} className={i % 2 === 0 ? 'print-tr-even' : 'print-tr-odd'}>
                    <td className="print-td-left">{row.telecaller_name}</td>
                    <td className="print-td-right">{num(row.total_call_attempts)}</td>
                    <td className="print-td-right">{num(row.unique_leads_called)}</td>
                    <td className="print-td-right" style={{ color: '#10b981', fontWeight: 600 }}>{num(row.connected_calls)}</td>
                    <td className="print-td-right" style={{ color: '#e11d48', fontWeight: 600 }}>{num(row.not_connected_calls)}</td>
                    <td className="print-td-right">{num(row.connection_rate)}%</td>
                    <td className="print-td-right">{num(row.new_lead_call_attempts)}</td>
                    <td className="print-td-right">{num(row.old_assigned_today_attempts)}</td>
                    <td className="print-td-right">{num(row.follow_up_call_attempts)}</td>
                    <td className="print-td-right">{num(row.assigned_today)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="print-empty">No agent activity today.</div>
          )}

          <div className="print-page-footer">
            <span>Santaan CRM · EOD Performance Report · {dateStr}</span>
          </div>
        </div>

        {/* ===== PAGE 5: Lead Journey Funnel + Attribution (schema v3) ===== */}
        {(journey && journey.total_leads > 0) || (attribution && (attribution.campaigns.length > 0 || attribution.ads.length > 0)) ? (
          <div className="eod-print-page">
            <div className="print-section-label">
              <span className="print-section-label-bar" />
              Journey & Attribution
            </div>
            <p className="print-section-subtitle">Lead funnel and paid attribution performance</p>

            {journey && journey.total_leads > 0 && (
              <>
                <h3 className="print-h3">Lead Journey Funnel</h3>
                <table className="print-table">
                  <thead>
                    <tr>
                      <th className="print-th-left">Stage</th>
                      <th className="print-th-right">Count</th>
                      <th className="print-th-right">Fallout</th>
                      <th className="print-th-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="print-tr-even"><td className="print-td-left">Total Leads</td><td className="print-td-right">{journey.total_leads}</td><td className="print-td-left">Unassigned</td><td className="print-td-right">{journey.unassigned}</td></tr>
                    <tr className="print-tr-odd"><td className="print-td-left">Assigned</td><td className="print-td-right">{journey.assigned}</td><td className="print-td-left">Not Called</td><td className="print-td-right">{journey.not_called}</td></tr>
                    <tr className="print-tr-even"><td className="print-td-left">Called</td><td className="print-td-right">{journey.called}</td><td className="print-td-left">{journey.no_patient_outcome !== undefined ? 'Patient No Answer' : 'Called, Not Connected'}</td><td className="print-td-right">{journey.not_connected}</td></tr>
                    <tr className="print-tr-odd"><td className="print-td-left">Connected</td><td className="print-td-right">{journey.connected}</td><td className="print-td-left">Lost</td><td className="print-td-right" style={{ color: '#e11d48', fontWeight: 600 }}>{journey.lost}</td></tr>
                    <tr className="print-tr-even"><td className="print-td-left">Marked Qualified</td><td className="print-td-right">{journey.qualified}</td><td className="print-td-left">Still Open</td><td className="print-td-right">{journey.still_open}</td></tr>
                    <tr className="print-tr-odd"><td className="print-td-left">Won (status)</td><td className="print-td-right">{journey.won}</td><td className="print-td-left">No Patient Outcome</td><td className="print-td-right">{journey.no_patient_outcome ?? '—'}</td></tr>
                    <tr className="print-tr-even"><td className="print-td-left">Positive (human-confirmed)</td><td className="print-td-right">{journey.qualified_positive ?? '—'}</td><td className="print-td-left" style={{ fontWeight: 600 }}>Registered</td><td className="print-td-right" style={{ color: '#10b981', fontWeight: 600 }}>{journey.registered ?? '—'}</td></tr>
                  </tbody>
                </table>
              </>
            )}

            {attribution && attribution.campaigns.length > 0 && (
              <>
                <h3 className="print-h3" style={{ marginTop: '24px' }}>Attribution Performance — Campaigns</h3>
                <table className="print-table">
                  <thead>
                    <tr>
                      <th className="print-th-left">Campaign</th>
                      <th className="print-th-right">Leads</th>
                      <th className="print-th-right">Called</th>
                      <th className="print-th-right">Conn.</th>
                      <th className="print-th-right">Won (status)</th>
                      <th className="print-th-right">Reg.</th>
                      <th className="print-th-right">Lost</th>
                      <th className="print-th-right">Reg %</th>
                      <th className="print-th-right">Lost %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attribution.campaigns.slice(0, 12).map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'print-tr-even' : 'print-tr-odd'}>
                        <td className="print-td-left">{sourceLabel(row.campaign_name ?? row.campaign_id ?? 'Unknown')}</td>
                        <td className="print-td-right">{row.total_leads}</td>
                        <td className="print-td-right">{row.called}</td>
                        <td className="print-td-right">{row.connected}</td>
                        <td className="print-td-right">{row.won}</td>
                        <td className="print-td-right" style={{ color: '#10b981', fontWeight: 600 }}>{row.registered ?? '—'}</td>
                        <td className="print-td-right" style={{ color: '#e11d48', fontWeight: 600 }}>{row.lost}</td>
                        <td className="print-td-right">{row.registration_rate !== undefined ? `${row.registration_rate}%` : '—'}</td>
                        <td className="print-td-right">{row.rejection_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {insights.length > 0 && (
              <>
                <h3 className="print-h3" style={{ marginTop: '24px' }}>Executive Insights</h3>
                {insights.map((ins, i) => (
                  <div key={i} style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '12px', color: '#17213A' }}>{ins.title}</div>
                    {ins.details.map((d, j) => (
                      <div key={j} style={{ fontSize: '11px', color: '#64748b', padding: '2px 0' }}>
                        • {d.label} — {d.metric_label}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}

            <div className="print-page-footer">
              <span>Santaan CRM · EOD Performance Report · {dateStr}</span>
            </div>
          </div>
        ) : null}
      </div>
    </>,
    document.body,
  );
}

// ============================================================================
// PRINT CSS — premium executive design, full color, multi-page A4
// ============================================================================

export const PRINT_CSS = `
/* ===== SCREEN: hide the print report entirely ===== */
.eod-print-report {
  display: none;
}

/* ===== PRINT: show only the print report, hide app UI ===== */
@media print {
  /* Force color preservation globally */
  *,
  *::before,
  *::after {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Hide the entire app root */
  #root {
    display: none !important;
  }

  /* Show the print report in normal document flow */
  .eod-print-report {
    display: block !important;
    position: static !important;
    width: 100% !important;
  }

  @page {
    size: A4 portrait;
    margin: 10mm 12mm 12mm 12mm;
  }

  .eod-print-report {
    font-family: -apple-system, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
    color: #1e293b;
    background: white;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    font-size: 11px;
    line-height: 1.5;
  }

  /* Page sections */
  .eod-print-page {
    break-after: page;
    page-break-after: always;
  }
  .eod-print-page:last-child {
    break-after: auto;
    page-break-after: auto;
  }

  /* Small logical blocks get break-inside: avoid */
  .print-kpi-grid,
  .print-reason-block,
  .print-key-finding,
  .print-flow-row,
  .print-calling-grid {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* Agent table: natural pagination, repeat header */
  .print-agent-table thead {
    display: table-header-group;
  }
  .print-agent-table tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* ===== Premium Header Banner ===== */
  .print-header-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: white;
    border-radius: 12px;
    padding: 18px 24px;
    margin-bottom: 6px;
  }
  .print-header-brand {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .print-brand-mark {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 800;
    color: white;
    letter-spacing: -0.02em;
  }
  .print-brand-name {
    font-size: 16px;
    font-weight: 700;
    color: white;
    letter-spacing: -0.02em;
  }
  .print-brand-sub {
    font-size: 10px;
    color: #94a3b8;
    margin-top: 1px;
  }
  .print-header-meta {
    text-align: right;
  }
  .print-header-meta h1 {
    font-size: 19px;
    font-weight: 700;
    color: white;
    margin: 0;
    letter-spacing: -0.02em;
  }
  .print-date {
    font-size: 12px;
    font-weight: 600;
    color: #cbd5e1;
    margin-top: 3px;
  }
  .print-time {
    font-size: 10px;
    color: #94a3b8;
    margin-top: 1px;
  }
  .print-generated-bar {
    font-size: 9px;
    color: #94a3b8;
    text-align: right;
    margin-bottom: 20px;
    padding-right: 4px;
  }

  /* ===== Section Labels ===== */
  .print-section-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-top: 20px;
    margin-bottom: 10px;
    letter-spacing: -0.01em;
  }
  .print-section-label-bar {
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background: linear-gradient(180deg, #3b82f6 0%, #8b5cf6 100%);
    flex-shrink: 0;
  }
  .print-section-spacing {
    margin-top: 28px;
  }
  .print-section-subtitle {
    font-size: 10px;
    color: #64748b;
    margin: 0 0 14px 0;
  }
  .print-h3 {
    font-size: 13px;
    font-weight: 700;
    color: #17213A;
    margin: 0 0 8px 0;
  }
  .print-subsection-title {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin-top: 20px;
    margin-bottom: 8px;
  }

  /* ===== KPI Grid ===== */
  .print-kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .print-kpi-card {
    border: 1px solid #e2e8f0;
    border-left: 4px solid #64748b;
    border-radius: 10px;
    padding: 12px 14px;
  }
  .print-kpi-label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
  }
  .print-kpi-value {
    font-size: 26px;
    font-weight: 800;
    margin-top: 4px;
    line-height: 1;
    letter-spacing: -0.02em;
  }
  .print-kpi-sub {
    font-size: 9px;
    color: #94a3b8;
    margin-top: 4px;
    font-weight: 500;
  }

  /* ===== Lead Flow ===== */
  .print-flow-container {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 18px 20px;
  }
  .print-flow-total {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 14px;
  }
  .print-flow-total-num {
    font-size: 36px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1;
    letter-spacing: -0.03em;
  }
  .print-flow-total-label {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
  }
  .print-flow-connector {
    width: 2px;
    height: 14px;
    background: #cbd5e1;
    margin: 0 0 10px 50px;
    border-radius: 1px;
  }
  .print-flow-outcomes {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  .print-flow-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .print-flow-label {
    width: 85px;
    text-align: right;
    font-size: 10px;
    font-weight: 600;
    color: #475569;
    flex-shrink: 0;
  }
  .print-flow-bar-track {
    flex: 1;
    min-width: 0;
  }
  .print-flow-bar {
    height: 26px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    min-width: 55px;
  }
  .print-flow-bar-num {
    font-size: 11px;
    font-weight: 700;
    color: white;
  }
  .print-flow-count {
    width: 32px;
    text-align: right;
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
    flex-shrink: 0;
  }
  .print-flow-pct {
    width: 42px;
    text-align: right;
    font-size: 10px;
    color: #94a3b8;
    flex-shrink: 0;
    font-weight: 500;
  }

  /* ===== Reason Blocks ===== */
  .print-reason-block {
    margin-top: 14px;
    border: 1px solid #e2e8f0;
    border-top: 3px solid #94a3b8;
    border-radius: 10px;
    overflow: hidden;
  }
  .print-reason-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
  }
  .print-reason-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
  }
  .print-reason-status {
    font-size: 13px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.01em;
  }
  .print-reason-count {
    font-size: 10px;
    color: #64748b;
    margin-left: 6px;
    font-weight: 500;
  }
  .print-reason-body {
    padding: 10px 14px 14px 14px;
  }
  .print-reason-question {
    font-size: 10px;
    color: #64748b;
    margin-bottom: 10px;
    font-weight: 500;
  }
  .print-reason-empty {
    font-size: 10px;
    color: #94a3b8;
    padding: 14px;
    background: #f8fafc;
    border-radius: 8px;
    text-align: center;
  }
  .print-reason-bars {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  .print-reason-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .print-reason-name {
    width: 170px;
    font-size: 10px;
    font-weight: 600;
    color: #334155;
    flex-shrink: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .print-reason-bar-track {
    flex: 1;
    min-width: 0;
    background: #f1f5f9;
    border-radius: 4px;
    height: 16px;
  }
  .print-reason-bar {
    height: 16px;
    border-radius: 4px;
  }
  .print-reason-num {
    width: 26px;
    text-align: right;
    font-size: 12px;
    font-weight: 800;
    color: #0f172a;
    flex-shrink: 0;
  }
  .print-reason-pct {
    width: 38px;
    text-align: right;
    font-size: 10px;
    color: #94a3b8;
    flex-shrink: 0;
    font-weight: 500;
  }

  /* ===== Key Finding ===== */
  .print-key-finding {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 12px;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #94a3b8;
    border-radius: 8px;
    background: #f8fafc;
  }
  .print-key-finding-label {
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .print-key-finding-text {
    font-size: 10px;
    color: #334155;
    font-weight: 500;
  }

  /* ===== Calling Metrics ===== */
  .print-calling-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }
  .print-metric {
    border: 1px solid #e2e8f0;
    border-left: 4px solid #64748b;
    border-radius: 10px;
    padding: 12px 14px;
  }
  .print-metric-label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
  }
  .print-metric-value {
    font-size: 22px;
    font-weight: 800;
    margin-top: 4px;
    line-height: 1;
    letter-spacing: -0.02em;
  }

  /* ===== Tables ===== */
  .print-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
    margin-top: 8px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
  }
  .print-table thead {
    background: #0f172a;
  }
  .print-th-left {
    text-align: left;
    padding: 9px 10px;
    font-weight: 700;
    color: #e2e8f0;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .print-th-right {
    text-align: right;
    padding: 9px 10px;
    font-weight: 700;
    color: #e2e8f0;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .print-td-left {
    text-align: left;
    padding: 8px 10px;
    font-weight: 700;
    color: #0f172a;
    border-bottom: 1px solid #f1f5f9;
  }
  .print-td-right {
    text-align: right;
    padding: 8px 10px;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
    font-weight: 500;
  }
  .print-tr-even {
    background: #ffffff;
  }
  .print-tr-odd {
    background: #f8fafc;
  }

  .print-empty {
    font-size: 11px;
    color: #94a3b8;
    padding: 20px;
    text-align: center;
    background: #f8fafc;
    border-radius: 10px;
  }

  /* ===== Page Footer ===== */
  .print-page-footer {
    margin-top: 40px;
    padding-top: 10px;
    border-top: 2px solid #0f172a;
    font-size: 8px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
}
`;
